import { Button, ButtonGroup } from "@components/button";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import {
    ISelectionWithEditableAmountCustomTableData
} from "@components/smart/selectionTableView/SelectionTableView.utils";
import SelectionTableViewDialog, {
    IProps as ISelectionTableViewDialogProps
} from "@components/smart/selectionTableView/SelectionTableViewDialog";
import { ActionType, addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { getExpandedEnumProps, setBoundValue } from "@odata/Data.utils";
import { getEntitySetByDocumentType } from "@odata/EntityTypes";
import {
    EntitySetName,
    EntityTypeName,
    IDocumentEntity,
    IPrEmploymentTemplateExtraSalaryEntity,
    IPrEmploymentTemplateSalaryComponentEntity,
    IPrEmploymentTypeEntity,
    IPrExtraSalaryEntity,
    IPrPayslipEmploymentExtraSalaryEntity,
    IPrPayslipEmploymentSalaryComponentEntity,
    IPrPayslipEntity,
    IPrPayslipLiabilityReceivableEntity,
    IPrSalaryComponentEntity,
    PrEmploymentEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryTemporalCurrentEntity,
    PrExtraSalaryTypeEntity,
    PrPayslipDeductionEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentExtraSalaryEntity,
    PrPayslipEmploymentItemEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity,
    PrPayslipInsuranceBenefitEntity,
    PrPayslipItemEntity,
    PrPayslipLiabilityReceivableEntity,
    PrSalaryComponentEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode, PrExtraSalaryTypeCode } from "@odata/GeneratedEnums";
import { universalPersonNameFormatter } from "@pages/admin/users/Users.utils";
import { getAgendaCountryCode } from "@pages/companies/Company.shared.utils";
import { IGetDefinition } from "@pages/PageUtils";
import { getPublicHolidays } from "@pages/payroll/attendance/Attendance.utils";
import { TEmploymentLineItemProp } from "@pages/payroll/employment/Employment.utils";
import {
    getDefinitionsFactory as getPickExtraSalaryDefinitionsFactory
} from "@pages/payroll/extraSalary/ExtraSalaryDef";
import PickExtraSalaryTableView from "@pages/payroll/extraSalary/PickExtraSalaryTableView";
import { IFormTableListSection } from "@pages/payroll/payslip/formTableList/FormTableList";
import { getEditableSections } from "@pages/payroll/payslip/formTableList/FormTableList.utils";
import FormTableListEditDialog from "@pages/payroll/payslip/formTableList/FormTableListEditDialog";
import { getTotalOverviewSections } from "@pages/payroll/payslip/overview/TotalOverview.utils";
import {
    CurrentPeriodHolidaysPath,
    CurrentPeriodWorkDaysPath,
    extraSalariesGroupId,
    PayslipFormAction,
    salaryComponentsGroupId,
    ToPayOutPath
} from "@pages/payroll/payslip/Payslip.def";
import {
    currentEmploymentPath,
    DialogType,
    getPayslipEmploymentTabId,
    setCurrentPayslipEmploymentTabId
} from "@pages/payroll/payslip/Payslip.utils";
import { getDefinitionsFactory } from "@pages/payroll/payslip/PayslipPairDocumentTableDef";
import PayslipPairDocumentTableView from "@pages/payroll/payslip/PayslipPairDocumentTableView";
import PickSalaryComponentTableView from "@pages/payroll/salaryComponent/PickSalaryComponentTableView";
import {
    getDefinitionsFactory as getPickSalaryComponentDefinitionsFactory
} from "@pages/payroll/salaryComponent/SalaryComponentDef";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import memoizeOne from "@utils/memoizeOne";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React from "react";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { formatCurrency } from "../../../types/Currency";
import DateType, { DateFormat, getUtcDayjs } from "../../../types/Date";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import { getEntityETag } from "../../../views/formView/Form.utils";
import { isDefined } from "@utils/general";

interface IPrPayslipCustomData extends ISelectionWithEditableAmountCustomTableData {
    editableSections: IFormTableListSection[];
}

class PayslipFormView extends FormViewForExtend<IPrPayslipEntity, IFormViewProps<IPrPayslipEntity, IPrPayslipCustomData> & WithConfirmationDialog> {
    _dialogType: DialogType;

    _overrideColumnPaths = [
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.AverageSalary),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Amount),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.Value),
        createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Amount),
        createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.Amount),
        createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.SymbolVariable),
        createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.SymbolSpecific),
        createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.SymbolConstant),
        createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.RemittanceInformation),
    ];

    get currentPeriodDayjs() {
        // return first day of the month - note, do not use actual day,
        // as it may behave differently according to current day in month, e.g. 31.1. -> February
        return getUtcDayjs().set("date", 1).set("month", this.entity.Month - 1).set("year", this.entity.Year);
    }

    get currentEmploymentBc(): BindingContext {
        const { storage } = this.props;
        return storage.data.bindingContext.navigate(PrPayslipEntity.Employments)
                .addKey(storage.getValueByPath(currentEmploymentPath));
    }

    getFormSubtitle = (): string => {
        const date = this.currentPeriodDayjs.toDate();
        return `/${DateType.format(date, DateFormat.monthAndYear)}/`;
    }

    async onAfterLoad(): Promise<void> {
        if (this.entity.Employments?.length) {
            setCurrentPayslipEmploymentTabId(this.props.storage, getPayslipEmploymentTabId(this.entity.Employments[0]));
        }

        // copy some default values from original column to override column
        this.copyValuesToOverrideColumn(this._overrideColumnPaths);
        this.setToPayoutValue();
        await this.calculatePeriodInfo();

        return super.onAfterLoad();
    }

    async calculatePeriodInfo(): Promise<void> {
        const { storage } = this.props;
        const companyCountry = getAgendaCountryCode(storage.context);
        const publicHolidays = await getPublicHolidays(companyCountry, this.entity.Year);
        const current = this.currentPeriodDayjs.startOf("month");
        const lastMonthDay = current.clone().endOf("month");
        let workDays = 0;
        let holidays = 0;
        let day = current;
        while (day.isBefore(lastMonthDay)) {
            if (day.day() !== 0 && day.day() !== 6) {
                const isHoliday = publicHolidays.find(h => h.isSame(day, "day"));
                if (isHoliday) {
                    holidays++;
                } else {
                    workDays++;
                }
            }
            day = day.add(1, "day");
        }
        storage.setValueByPath(CurrentPeriodWorkDaysPath, workDays);
        storage.setValueByPath(CurrentPeriodHolidaysPath, holidays);
    }

    setToPayoutValue(): void {
        const { storage } = this.props;
        this.entity.Employments?.forEach(employment => {
            if (employment.IsIncludingOvertime) {
                const overtime = employment.ExtraSalaries.find(extra => extra.Type?.Code === PrExtraSalaryTypeCode.Overtime);
                const bc = storage.data.bindingContext
                        .navigate(PrPayslipEntity.Employments).addKey(employment)
                        .navigate(PrPayslipEmploymentEntity.ExtraSalaries).addKey(overtime)
                        .navigate(ToPayOutPath);
                storage.setValue(bc, true);
            }
        });
    }

    setIsIncludingOvertime(data: IPrPayslipEntity): void {
        // set IsIncludingOvertime value from localContext field
        data.Employments?.forEach(employment => {
            const overtime = employment.ExtraSalaries.find(extra => extra.Type?.Code === PrExtraSalaryTypeCode.Overtime);
            if ((overtime as unknown as IEntity)[ToPayOutPath]) {
                employment.IsIncludingOvertime = true;
            }
        });
    }

    copyValuesToOverrideColumn(paths: string[]): void {
        const { storage } = this.props;
        BindingContext.each(storage.data.entity, storage.data.bindingContext, (value, bindingContext) => {
            if (paths.includes(bindingContext.getNavigationPath(true))) {
                // should be copied
                const prop = bindingContext.getPath(true);
                const overrideBc = bindingContext.getParent().navigate(`${prop}Override`);
                const currentVal = storage.getValue(overrideBc);
                if (!currentVal) {
                    storage.setValue(overrideBc, value);
                }
            }
            return true;
        });
    }

    clearNotOverridenFields(paths: string[], data: IPrPayslipEntity): IPrPayslipEntity {
        const { storage } = this.props;
        BindingContext.each(data, storage.data.bindingContext, (originalVal, bindingContext) => {
            if (paths.includes(bindingContext.getNavigationPath(true))) {
                const prop = bindingContext.getPath(true);
                const overrideBc = bindingContext.getParent().navigate(`${prop}Override`);
                const currentVal = storage.getValue(overrideBc);
                if (currentVal === originalVal) {
                    setBoundValue({ data, bindingContext: overrideBc, newValue: undefined, preventCloning: true, dataBindingContext: storage.data.bindingContext });
                }
            }
            return true;
        });
        return data;
    }

    handleChange(e: ISmartFieldChange): void {
        this.props.storage.handleChange(e);
        const shouldUpdate = e.triggerAdditionalTasks !== false;
        this.props.storage.refreshFields(shouldUpdate);
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case PayslipFormAction.ConnectReceivables:
            case PayslipFormAction.ConnectLiabilities:
                const dialogType = actionId === PayslipFormAction.ConnectReceivables
                        ? DialogType.Receivables
                        : DialogType.Liabilities;
                // check if form is changed, if so, ask user what to do
                this._dialogType = dialogType;
                this.forceUpdate();
                break;
            case PayslipFormAction.PastCorrections:
                window.alert("Not implemented yet...");
                break;
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    handleLineItemsChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        storage.handleLineItemsChange(e);

        const isSalaryComponentType = e.bindingContext.getParent()?.getPath(true) === PrPayslipEmploymentEntity.SalaryComponents
                && e.bindingContext.getPath() === PrPayslipEmploymentSalaryComponentEntity.Type;
        const isExtraSalaryComputationType = e.bindingContext.getParent()?.getPath(true) === PrPayslipEmploymentEntity.ExtraSalaries
                && e.bindingContext.getPath() === PrPayslipEmploymentExtraSalaryEntity.ComputationType;

        if (isSalaryComponentType || isExtraSalaryComputationType) {
            // refresh whole group to show fields based on the selected type
            storage.addActiveGroup(e.bindingContext);
        }

        const shouldUpdate = e.triggerAdditionalTasks !== false;
        storage.refreshFields(shouldUpdate);
    }

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent<IPrEmploymentTemplateExtraSalaryEntity | IPrEmploymentTemplateSalaryComponentEntity>): void => {
        if (args.actionType === ActionType.Custom) {
            switch (args.customActionType) {
                case DialogType.SalaryComponent:
                case DialogType.ExtraSalary:
                    this._dialogType = args.customActionType as DialogType;
                    break;

                default:
                    throw new Error(`Unknown custom actionType ${args.customActionType}`);
            }
            this.forceUpdate();
        } else {
            if (args.actionType === ActionType.Add
                    && (args.groupId === salaryComponentsGroupId || args.groupId === extraSalariesGroupId)) {
                args.affectedItems.forEach((item: IPrPayslipEmploymentSalaryComponentEntity, idx: number) => {
                    item.IsOneTime = true;
                });
            }

            this.props.storage.handleLineItemsAction(args);
            this.props.storage.refresh();
        }
    };

    handleExtraSalarySelection = async (selected: BindingContext[], entities: IPrExtraSalaryEntity[]): Promise<void> => {
        const { storage } = this.props;
        const { oData } = storage;

        const extraSalaryBc = this.currentEmploymentBc
                .navigate(PrPayslipEmploymentEntity.ExtraSalaries);
        const ids = entities.map(e => e.Id);

        const query = oData.getEntitySetWrapper(EntitySetName.PrExtraSalaries).query()
                .select(PrExtraSalaryEntity.Id)
                .expand(PrExtraSalaryEntity.Type, (q) =>
                        q.select(PrExtraSalaryTypeEntity.IsLegallyRequired, PrExtraSalaryTypeEntity.Code))
                .expand(PrExtraSalaryEntity.CurrentTemporalPropertyBag, (q) =>
                        q.select(PrExtraSalaryTemporalCurrentEntity.Name, PrExtraSalaryTemporalCurrentEntity.ComputationTypeCode, PrExtraSalaryTemporalCurrentEntity.Value))
                .filter(`Id in (${ids.join(",")})`);

        const extraSalaries = (await query.fetchData<IPrExtraSalaryEntity[]>()).value;

        extraSalaries.forEach((entity: IPrExtraSalaryEntity, idx) => {
            const newExtraSalary: Partial<IPrPayslipEmploymentExtraSalaryEntity> = {
                ...entity.CurrentTemporalPropertyBag,
                ...getExpandedEnumProps(EntityTypeName.PrExtraSalaryType, entity.Type?.Code, "Type", entity.Type),
                ...getExpandedEnumProps(EntityTypeName.PrExtraSalaryComputationType, entity.CurrentTemporalPropertyBag.ComputationTypeCode, "ComputationType"),
                ValueOverride: entity.CurrentTemporalPropertyBag.Value,
                IsOneTime: true,
            };
            const path = extraSalaryBc.getNavigationPath(false);
            addNewLineItem(storage, path, extraSalariesGroupId, [], newExtraSalary);
        });

        storage.refresh();
    }

    handleSalaryComponentSelection = async (selected: BindingContext[], entities: IPrSalaryComponentEntity[]): Promise<void> => {
        const { storage } = this.props;
        const { oData } = storage;

        const salaryComponentsBc = this.currentEmploymentBc
                .navigate(PrPayslipEmploymentEntity.SalaryComponents);
        const ids = entities.map(e => e.Id);

        const query = oData.getEntitySetWrapper(EntitySetName.PrSalaryComponents).query()
                .select(PrSalaryComponentEntity.Id)
                .expand(PrSalaryComponentEntity.CurrentTemporalPropertyBag)
                .filter(`Id in (${ids.join(",")})`);

        const salaryComponents = (await query.fetchData<IPrSalaryComponentEntity[]>()).value;
        salaryComponents.forEach((entity: IPrSalaryComponentEntity, idx) => {
            const newSalaryComponent: IPrPayslipEmploymentSalaryComponentEntity = {
                ...entity.CurrentTemporalPropertyBag,
                ...getExpandedEnumProps(EntityTypeName.PrSalaryComponentType, entity.CurrentTemporalPropertyBag?.TypeCode, "Type"),
                ...getExpandedEnumProps(EntityTypeName.PrRegularSalaryType, entity.CurrentTemporalPropertyBag?.RegularSalaryTypeCode, "RegularSalaryType"),
                ...getExpandedEnumProps(EntityTypeName.PrFixedBonusTaxImpact, entity.CurrentTemporalPropertyBag?.FixedBonusTaxImpactCode, "FixedBonusTaxImpact"),
                ...getExpandedEnumProps(EntityTypeName.PrFixedBonusPayOutImpact, entity.CurrentTemporalPropertyBag?.FixedBonusPayOutImpactCode, "FixedBonusPayOutImpact"),
                IsOneTime: true
            };
            const path = salaryComponentsBc.getNavigationPath(false);
            addNewLineItem(storage, path, salaryComponentsGroupId, [], newSalaryComponent);
        });

        storage.refresh();
    }

    handleDocumentSelection = (selected: BindingContext[], entities: IDocumentEntity[], tableStorage: TSmartODataTableStorage): void => {
        const { storage } = this.props;
        const current = storage.getValueByPath(PrPayslipEntity.LiabilitiesAndReceivables);
        const documentTypeCode = this._dialogType === DialogType.Receivables ? DocumentTypeCode.OtherReceivable : DocumentTypeCode.OtherLiability;
        const itemsOfOtherType = current.filter((item: IPrPayslipLiabilityReceivableEntity) => item.Document.DocumentTypeCode !== documentTypeCode);

        const { pairedItems } = (tableStorage as TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>).getCustomData();

        const newItems: IPrPayslipLiabilityReceivableEntity[] = [];
        entities.forEach((entity, idx) => {
            const docType = entity.DocumentTypeCode;
            const entitySetName = getEntitySetByDocumentType(docType);
            const entityType = storage.oData.getMetadata().entitySets[entitySetName].getType();

            const currentItem = current.find((item: IPrPayslipLiabilityReceivableEntity) => item.Document.Id === entity.Id && item.Document.DocumentTypeCode === docType);
            const pairedItem = pairedItems[`${entitySetName}(${entity.Id})`];

            const pairedProps = {
                Amount: pairedItem.Amount,
                ExchangeRatePerUnit: pairedItem.ExchangeRatePerUnit,
                TransactionAmount: pairedItem.TransactionAmount,
            };

            if (!currentItem) {
                newItems.push(BindingContext.createNewEntity(current.length + idx, {
                    Document: {
                        Id: entity.Id,
                        _metadata: {
                            "": { type: `#${entityType.getFullName()}` }
                        },
                        Amount: entity.Amount,
                        NumberOurs: entity.NumberOurs,
                        ...getExpandedEnumProps(EntityTypeName.Currency, entity.CurrencyCode),
                        ...getExpandedEnumProps(EntityTypeName.DocumentType, docType),
                    },
                    ...getExpandedEnumProps(EntityTypeName.Currency, entity.CurrencyCode),
                    ...getExpandedEnumProps(EntityTypeName.Currency, pairedItem.TransactionCurrency?.Code, PrPayslipLiabilityReceivableEntity.TransactionCurrency),
                    ...pairedProps
                }));
            } else {
                newItems.push({
                    ...currentItem,
                    ...pairedProps
                });
            }
        });
        storage.setValueByPath(PrPayslipEntity.LiabilitiesAndReceivables, [...itemsOfOtherType, ...newItems]);
    }

    handleCloseDialog = (): void => {
        this._dialogType = null;
        this.forceUpdate();
    };

    forEachOverriddenItems(callback: (bc: BindingContext) => void) {
        const { storage } = this.props;
        // clear overridden items in payslip
        const payslipItemsBc = storage.data.bindingContext.navigate(PrPayslipEntity.Items);
        storage.data.entity.Items.forEach(item => {
            const itemBc = payslipItemsBc.addKey(item);
            callback(itemBc.navigate(PrPayslipItemEntity.DecimalValueOverride));
        });
        // clear overridden items in employments
        const employmentsBc = storage.data.bindingContext.navigate(PrPayslipEntity.Employments);
        storage.data.entity.Employments?.forEach(employment => {
            const employmentItemsBc = employmentsBc.addKey(employment).navigate(PrPayslipEmploymentEntity.Items);
            employment.Items?.forEach(item => {
                const itemBc = employmentItemsBc.addKey(item);
                callback(itemBc.navigate(PrPayslipEmploymentItemEntity.DecimalValueOverride));
                callback(itemBc.navigate(PrPayslipEmploymentItemEntity.IntValueOverride));
            });
        });
    }

    hasOverriddenItems = memoizeOne(() => {
        const { storage } = this.props;
        let hasOverriddenItem = false;
        this.forEachOverriddenItems((bc: BindingContext) => {
            if (isDefined(storage.getValue(bc))) {
                hasOverriddenItem = true;
            }
        });
        return hasOverriddenItem;
    }, () => [getEntityETag(this.props.storage)]);

    handleSaveManualChangesDialog = async (): Promise<void> => {
        const { storage } = this.props;
        // confirm temporal changes
        const bcs: BindingContext[] = [];
        this.forEachOverriddenItems((bc: BindingContext) => {
            bcs.push(bc);
        });
        // confirm temporal values
        await storage.confirmFields(bcs);
        // save form immediately
        await this.save();
        this.handleCloseDialog();
    }

    handleCloseManualChangesDialog = (): void => {
        const { storage } = this.props;
        // discard manual changes
        this.forEachOverriddenItems((bc: BindingContext) => {
            storage.cancelFields([bc]);
        });
        this.handleCloseDialog();
    }

    handleDiscardManualChanges = (): void => {
        const { storage } = this.props;
        // discard manual changes
        this.forEachOverriddenItems((bc: BindingContext) => {
            // just completely clear overridden value
            storage.clearValue(bc);
        });
        // save form immediately
        this.save();
    };

    handleManualChange = (): void => {
        const { storage } = this.props;
        storage.setCustomData({ editableSections: getEditableSections(getTotalOverviewSections(storage)) });
        this._dialogType = DialogType.PayslipManualChange;
        this.forceUpdate();
    };

    getAlreadySelectedItemIds(propName: TEmploymentLineItemProp): number[] {
        const { entity } = this.props.storage.data;
        const ids: number[] = [];
        return ids;
    }

    getSelectionTableViewHeaderData = memoizeOne((): IReadOnlyListItem[] => {
        const employeeBc = this.props.storage.data.bindingContext.navigate(PrPayslipEntity.Employee);
        const employee = this.props.storage.getValue(employeeBc);
        const employmentTypeBc = this.currentEmploymentBc
                .navigate(PrPayslipEmploymentEntity.Employment)
                .navigate(PrEmploymentEntity.Type);
        const type: IPrEmploymentTypeEntity = this.props.storage.getValue(employmentTypeBc);
        return [
            {
                label: i18next.t("Payslip:Form.Employee"),
                value: universalPersonNameFormatter(employee)
            },
            {
                label: i18next.t("Payslip:Form.Type"),
                value: type?.Name
            }
        ];
    }, () => [this.props.storage.data.entity.Id]);

    renderCustomDialogs(): React.ReactElement {
        const { storage } = this.props;
        let tableView;
        let getDef: IGetDefinition;
        let onSelection: (selected: BindingContext[], entities: IEntity[], tableStorage?: TSmartODataTableStorage) => void;
        const restProps: Partial<ISelectionTableViewDialogProps> = {};

        switch (this._dialogType) {
            case DialogType.SalaryComponent:
                tableView = PickSalaryComponentTableView;
                getDef = getPickSalaryComponentDefinitionsFactory(true);
                onSelection = this.handleSalaryComponentSelection;
                break;

            case DialogType.ExtraSalary:
                tableView = PickExtraSalaryTableView;
                getDef = getPickExtraSalaryDefinitionsFactory({
                    inEditableWindow: true,
                    filters: [
                        // legally required extra salaries are by default generated by the system - they cannot be added manually
                        `${PrExtraSalaryEntity.Type}/${PrExtraSalaryTypeEntity.IsLegallyRequired} neq true`
                    ]
                });
                onSelection = this.handleExtraSalarySelection;
                break;

            case DialogType.Receivables:
            case DialogType.Liabilities:
                tableView = PayslipPairDocumentTableView;
                const entitySet = this._dialogType === DialogType.Receivables ? EntitySetName.OtherReceivables : EntitySetName.OtherLiabilities;
                getDef = getDefinitionsFactory(entitySet);
                onSelection = this.handleDocumentSelection;
                const documentTypeCode = this._dialogType === DialogType.Receivables ? DocumentTypeCode.OtherReceivable : DocumentTypeCode.OtherLiability;
                const pairedDocuments = this.props.storage.getValueByPath(PrPayslipEntity.LiabilitiesAndReceivables);
                const pairedItems: ISelectionWithEditableAmountCustomTableData["pairedItems"] = {};
                restProps.selectedItems = [];
                pairedDocuments.forEach((item: IPrPayslipLiabilityReceivableEntity) => {
                    if (item.Document.DocumentTypeCode === documentTypeCode) {
                        const key = `${entitySet}(${item.Document.Id})`;
                        pairedItems[key] = {
                            TransactionAmount: item.TransactionAmount,
                            Amount: item.Amount,
                            TransactionCurrency: item.TransactionCurrency,
                            ExchangeRatePerUnit: item.ExchangeRatePerUnit
                        };
                        restProps.selectedItems.push(this.props.storage.data.bindingContext.createNewBindingContext(key));
                    }
                });
                restProps.initializeStorage = (storage) => {
                    const tableStorage = (storage as unknown as TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>);
                    tableStorage.setCustomData({ pairedItems });
                };
                break;
            case DialogType.PayslipManualChange:
                const { editableSections } = storage.getCustomData();
                return (
                        <FormTableListEditDialog sections={editableSections}
                                                 title={storage.t("Payslip:Form.UpdateCalculations")}
                                                 storage={storage}
                                                 onCancel={this.handleCloseManualChangesDialog}
                                                 onSave={this.handleSaveManualChangesDialog} />
                );
            default:
                // possible wrong configuration or no dialog configured
                return null;
        }
        return (
                <SelectionTableViewDialog storage={storage}
                                          tableView={tableView}
                                          getDefinition={getDef}
                                          title={storage.t("Payslip:Form.Title")}
                                          headerData={this.getSelectionTableViewHeaderData()}
                                          onSelection={onSelection}
                                          onClose={this.handleCloseDialog}
                                          {...restProps}
                                          isSingleSelect={false}/>
        );
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean) {
        super.onAfterSave(isNew, preventTableViewRefresh);
    }

    onBeforeSave(data?: IPrPayslipEntity): IPrPayslipEntity {
        data = super.onBeforeSave(data) ?? data ?? cloneDeep(this.entity);
        this.setIsIncludingOvertime(data);
        return this.clearNotOverridenFields(this._overrideColumnPaths, data);
    }

    onSaveFail() {
        // if (...todo...) {
        // this.showAmountExceedingSalaryDialog();
        // }
        super.onSaveFail();
    }

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;
        const hasOverriddenValues = this.hasOverriddenItems();
        const isSaveDisabled = this.shouldDisableSaveButton();
        const isCancelDisabled = this.shouldDisableCancelButton();

        return (
                <ButtonGroup wrap={"wrap"}>
                    <Button hotspotId={"formCancel"}
                            isDisabled={storage.isDisabled || isCancelDisabled}
                            onClick={this.handleFormCancel}
                            isTransparent>{storage.t("Common:General.Cancel")}</Button>
                    {!isFormReadOnly && (hasOverriddenValues ? (
                            <Button hotspotId={"discardManualChanges"}
                                    onClick={this.handleDiscardManualChanges}
                                    isDisabled={isSaveDisabled || !isCancelDisabled}
                                    isTransparent>{storage.t("Payslip:Form.DiscardManualChanges")}</Button>
                    ) : (
                            <Button hotspotId={"manualChange"}
                                    onClick={this.handleManualChange}
                                    isDisabled={isSaveDisabled || !isCancelDisabled}
                                    isTransparent>{storage.t("Payslip:Form.ChangeManually")}</Button>
                    ))}
                    {!isFormReadOnly &&
                            <Button hotspotId={"formSave"}
                                    onClick={this.handleSaveClick}
                                    isDisabled={isSaveDisabled || hasOverriddenValues}>
                                {storage.t("Payslip:Form.SaveAndRecalculate")}
                            </Button>
                    }
                </ButtonGroup>
        );
    }

    async showAmountExceedingSalaryDialog(): Promise<void> {
        const { storage } = this.props;

        // get newly calculated salary from error response
        // todo: get newly calculated salary from error response
        const salary = storage.data.alert?.detailData?.salary ?? 123;
        const message = i18next.t("Payslip:Form.AmountExceedingSalaryDialog", { salary: formatCurrency(salary, getCompanyCurrency(storage.context)) });

        // we are replacing standard error alert with confirmation dialog, where user can decide which action to take
        storage.setFormAlert(null);

        const shouldRemoveLinks = await this.props.confirmationDialog.open({
            content: message,
            dialogProps: {
                width: "416px"
            }
        });

        if (shouldRemoveLinks) {
            // user decided to remove links -> clear the list and try to save it again
            // ... todo...
        }
    }
}

export default withPermissionContext(withConfirmationDialog(PayslipFormView));